<script lang="ts">
  import { flip } from 'svelte/animate';
  import { onMount, tick } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { navigationIntent } from './lib/actions/navigation-intent';
  import type { DatabaseLike, DbState } from './lib/db/types';
  import ReaderStream from './lib/components/ReaderStream.svelte';
  import type { NavigationIntent } from './lib/actions/navigation-intent';
  import type { ReaderWorkspaceStore } from './lib/reader/workspace-storage';
  import SearchResultsList from './lib/components/SearchResultsList.svelte';
  import { ReaderState } from './lib/reader/reader-state.svelte';
  import type { ChapterListStreamInput, SearchResult } from './lib/reader/types';

  interface ReaderTestingApi {
    openStream(bookId: number, chapter: number, verse?: number | null): boolean;
    openChapterListStream(items: ChapterListStreamInput[], title?: string | null): boolean;
    openHornerReading(): boolean;
    setHornerReadingDay(day: number): boolean;
    toggleHornerChapterRead(bookId: number, chapter: number): void;
    closeStream(streamId: string): boolean;
    activateStream(index: number): boolean;
    flushWorkspacePersistence(): void;
  }

  type TestingWindow = Window &
    typeof globalThis & {
      __kjvreaderTesting?: ReaderTestingApi;
    };

  let {
    database,
    workspaceStorage,
  }: {
    database: DatabaseLike;
    workspaceStorage?: ReaderWorkspaceStore;
  } = $props();

  let reader = $state<ReaderState | null>(null);
  let searchInput = $state.raw<HTMLInputElement | null>(null);
  let settingsFab = $state.raw<HTMLButtonElement | null>(null);
  let settingsMenuFirstItem = $state.raw<HTMLButtonElement | null>(null);
  let hornerDayBadge = $state.raw<HTMLButtonElement | null>(null);
  let hornerDayInput = $state.raw<HTMLInputElement | null>(null);
  let carousel = $state.raw<HTMLDivElement | null>(null);
  let carouselScrollRequest: number | null = null;
  let programmaticCarouselIndex = $state<number | null>(null);
  let programmaticCarouselDistance = $state<number | null>(null);
  let skipCarouselSyncIndex = $state<number | null>(null);
  let hornerDayDialogOpen = $state(false);
  let hornerDayDraft = $state('1');
  let hornerDayError = $state<string | null>(null);

  const SEARCH_SHEET_ID = 'reader-search-sheet';
  const SETTINGS_MENU_PANEL_ID = 'reader-settings-menu';
  const HORNER_DAY_DIALOG_ID = 'horner-day-dialog';
  let updateStatusMessage = $derived(reader ? messageForUpdateStatus(reader.updateStatus) : null);
  let updateStatusIsError = $derived(reader ? isUpdateError(reader.updateStatus) : false);

  $effect(() => {
    const activeIndex = reader?.activeStreamIndex;
    if (activeIndex === undefined || activeIndex === null || !carousel) {
      return;
    }

    if (skipCarouselSyncIndex === activeIndex) {
      skipCarouselSyncIndex = null;
      return;
    }

    void waitForNextFrame().then(() => scrollCarouselToActiveStream(activeIndex));
  });

  onMount(() => {
    const nextReader = new ReaderState(database, workspaceStorage);
    reader = nextReader;

    const onKeydown = (event: KeyboardEvent) => {
      if (nextReader.settingsOpen && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
        event.preventDefault();
        focusSettingsMenuItem(event.key === 'ArrowDown' ? 1 : -1);
        return;
      }

      if (event.key === 'Tab') {
        if (hornerDayDialogOpen) {
          trapDialogFocus('.horner-day-dialog', event);
          return;
        }

        if (nextReader.navigatorOpen) {
          trapDialogFocus('.nav-sheet', event);
          return;
        }

        if (nextReader.searchOpen) {
          trapDialogFocus('.search-sheet', event);
          return;
        }
      }

      if (
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
        !event.repeat &&
        !nextReader.navigatorOpen &&
        !nextReader.searchOpen &&
        !nextReader.settingsOpen &&
        !isEditableTarget(event.target)
      ) {
        const moved = nextReader.moveActiveStream(event.key === 'ArrowRight' ? 1 : -1);
        if (moved) {
          event.preventDefault();
        }
        return;
      }

      if (event.key !== 'Escape') return;

      if (hornerDayDialogOpen) {
        closeHornerDayDialog({ restoreFocus: true });
        return;
      }

      if (nextReader.navigatorOpen) {
        nextReader.closeNavigator();
        return;
      }

      if (nextReader.settingsOpen) {
        closeSettingsMenu({ restoreFocus: true });
        return;
      }

      if (nextReader.searchOpen) {
        nextReader.closeSearchWidget();
      }
    };

    const onPopState = (event: PopStateEvent) => {
      nextReader.activateFromHistory(event.state);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        nextReader.flushWorkspacePersistence();
      }
    };

    const onWindowClick = (event: MouseEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      if (nextReader.settingsOpen && !target.closest('.settings-menu')) {
        closeSettingsMenu();
      }
    };

    window.addEventListener('keydown', onKeydown);
    window.addEventListener('popstate', onPopState);
    window.addEventListener('click', onWindowClick);
    document.addEventListener('visibilitychange', onVisibilityChange);

    exposeTestingApi(nextReader);
    void nextReader.init();

    return () => {
      nextReader.destroy();
      clearTestingApi();
      window.removeEventListener('keydown', onKeydown);
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('click', onWindowClick);
      document.removeEventListener('visibilitychange', onVisibilityChange);

      if (carouselScrollRequest !== null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(carouselScrollRequest);
      }
    };
  });

  function chooseChapter(chapter: number, intent: NavigationIntent = 'replace-current-stream'): void {
    if (!reader) return;

    if (intent === 'open-new-stream-right') {
      const book = reader.draftBook;
      if (!book) return;

      const target = reader.openStreamRight(book.id, chapter, {
        align: 'center',
        history: 'push',
      });
      if (target) {
        reader.closeNavigator();
      }
      return;
    }

    reader.chooseChapter(chapter);
  }

  async function toggleNavigator(): Promise<void> {
    reader?.toggleNavigator();

    if (reader?.navigatorOpen) {
      await tick();
      document.querySelector<HTMLElement>('.nav-sheet__close')?.focus();
    }
  }

  async function toggleSearch(): Promise<void> {
    if (!reader) return;

    if (reader.searchOpen) {
      closeSearchSheet();
      return;
    }

    reader.openSearchWidget();
    await tick();
    searchInput?.focus();
  }

  async function toggleSettingsMenu(): Promise<void> {
    if (!reader) return;

    if (reader.settingsOpen) {
      closeSettingsMenu({ restoreFocus: true });
      return;
    }

    reader.toggleSettingsMenu();
    if (reader.settingsOpen) {
      await tick();
      settingsMenuFirstItem?.focus();
    }
  }

  function closeSearchSheet(): void {
    reader?.closeSearchWidget();
  }

  async function clearSearch(): Promise<void> {
    reader?.clearSearch({ keepOpen: true });
    await tick();
    searchInput?.focus();
  }

  function selectSearchResult(
    result: SearchResult,
    intent: NavigationIntent = 'replace-current-stream',
  ): void {
    if (!reader) return;

    if (intent === 'open-new-stream-right') {
      const target = reader.openStreamRight(result.bookId, result.chapter, {
        targetVerse: result.verse,
        align: 'top',
        history: 'push',
      });
      if (target) {
        reader.closeSearchWidget();
      }
      return;
    }

    reader.navigateToVerse(result.bookId, result.chapter, result.verse, {
      closeSearch: true,
    });
  }

  function navigateToCrossReference(reference: {
    bookId: number;
    chapter: number;
    verse: number;
  }, intent: NavigationIntent = 'replace-current-stream'): void {
    if (!reader) return;

    if (intent === 'open-new-stream-right') {
      reader.openStreamRight(reference.bookId, reference.chapter, {
        targetVerse: reference.verse,
        align: 'top',
        history: 'push',
      });
      return;
    }

    reader.navigateToVerse(reference.bookId, reference.chapter, reference.verse);
  }

  function switchHands(): void {
    reader?.toggleNavHandedness();
    closeSettingsMenu({ restoreFocus: true });
  }

  function checkForUpdates(): void {
    closeSettingsMenu({ restoreFocus: true });
    void reader?.checkForUpdates(checkForApplicationUpdate);
  }

  function openHornerReading(): void {
    closeSettingsMenu();
    reader?.openHornerReading();
  }

  function hornerProgressItems(nextReader: ReaderState): Array<{
    key: string;
    label: string;
    read: boolean;
  }> {
    const stream = nextReader.activeStream;
    if (!stream || !nextReader.isHornerStream(stream)) return [];

    return stream.sequence.items.map((item, index) => ({
      key: `${item.bookId}:${item.chapter}:${index}`,
      label: item.label ?? `Reading ${index + 1}`,
      read: nextReader.isHornerChapterRead(item.bookId, item.chapter),
    }));
  }

  async function openHornerDayDialog(): Promise<void> {
    const day = reader?.activeHornerDay;
    if (day === null || day === undefined) return;

    hornerDayDraft = String(day);
    hornerDayError = null;
    hornerDayDialogOpen = true;
    await tick();
    hornerDayInput?.focus();
    hornerDayInput?.select();
  }

  function closeHornerDayDialog(options: { restoreFocus?: boolean } = {}): void {
    if (!hornerDayDialogOpen) return;

    hornerDayDialogOpen = false;
    hornerDayError = null;
    if (options.restoreFocus) {
      void waitForNextFrame().then(() => hornerDayBadge?.focus());
    }
  }

  function updateHornerDayDraft(value: string): void {
    hornerDayDraft = value;
    hornerDayError = null;
  }

  function submitHornerDay(event: SubmitEvent): void {
    event.preventDefault();
    if (!reader) return;

    const day = Number(hornerDayDraft.trim());
    if (!Number.isInteger(day) || day < 1) {
      hornerDayError = 'Enter a whole day number of 1 or greater.';
      void tick().then(() => hornerDayInput?.focus());
      return;
    }

    const target = reader.setHornerReadingDay(day);
    if (!target) {
      hornerDayError = 'Unable to start the Horner reading day.';
      return;
    }

    const activeStreamIndex = reader.activeStreamIndex;
    closeHornerDayDialog({ restoreFocus: true });
    void waitForNextFrame().then(() => scrollCarouselToActiveStream(activeStreamIndex));
  }

  async function checkForApplicationUpdate(): Promise<boolean> {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return false;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;

    await registration.update();
    if (!registration.waiting) return false;

    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    return true;
  }

  function closeSettingsMenu(options: { restoreFocus?: boolean } = {}): void {
    if (!reader?.settingsOpen) return;

    reader.closeSettingsMenu();
    if (options.restoreFocus) {
      void waitForNextFrame().then(() => settingsFab?.focus());
    }
  }

  function focusSettingsMenuItem(direction: 1 | -1): void {
    const panel = document.getElementById(SETTINGS_MENU_PANEL_ID);
    if (!panel) return;

    const items = Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]'));
    if (items.length === 0) return;

    const activeIndex = Math.max(0, items.findIndex((item) => item === document.activeElement));
    const nextIndex = (activeIndex + direction + items.length) % items.length;
    items[nextIndex]?.focus();
  }

  function trapDialogFocus(selector: string, event: KeyboardEvent): void {
    const sheet = document.querySelector<HTMLElement>(selector);
    if (!sheet) return;

    const focusable = Array.from(
      sheet.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => !element.hasAttribute('disabled') && element.offsetParent !== null);

    if (focusable.length === 0) {
      event.preventDefault();
      sheet.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function waitForNextFrame(): Promise<void> {
    if (typeof requestAnimationFrame !== 'function') {
      return Promise.resolve();
    }

    return new Promise((resolve) => requestAnimationFrame(() => resolve()));
  }

  function isDownloading(status: DbState): status is { state: 'downloading'; progress: number } {
    return status.state === 'downloading';
  }

  function isError(status: DbState): status is { state: 'error'; message: string } {
    return status.state === 'error';
  }

  function isUpdateError(status: ReaderState['updateStatus']): status is { state: 'error'; message: string } {
    return status.state === 'error';
  }

  function messageForUpdateStatus(status: ReaderState['updateStatus']): string | null {
    switch (status.state) {
      case 'checking':
        return 'Checking for updates…';
      case 'up-to-date':
        return 'Already up to date.';
      case 'error':
        return status.message;
      default:
        return null;
    }
  }

  function handleCarouselScroll(): void {
    queueCarouselActiveUpdate();
  }

  function handleCarouselInteractionStart(): void {
    programmaticCarouselIndex = null;
    programmaticCarouselDistance = null;
  }

  function queueCarouselActiveUpdate(): void {
    if (carouselScrollRequest !== null) {
      return;
    }

    if (typeof requestAnimationFrame !== 'function') {
      updateActiveCarouselStream();
      return;
    }

    carouselScrollRequest = requestAnimationFrame(() => {
      carouselScrollRequest = null;
      updateActiveCarouselStream();
    });
  }

  function updateActiveCarouselStream(): void {
    if (!carousel || !reader || reader.streams.length <= 1) {
      return;
    }

    const panels = Array.from(carousel.querySelectorAll<HTMLElement>('[data-cy="reader-stream-panel"]'));
    if (panels.length === 0) {
      return;
    }

    const carouselRect = carousel.getBoundingClientRect();
    const viewportCenter = carousel.scrollLeft + carousel.clientWidth / 2;

    if (programmaticCarouselIndex !== null) {
      const targetPanel = carousel.querySelector<HTMLElement>(
        `[data-cy="reader-stream-panel"][data-stream-index="${programmaticCarouselIndex}"]`,
      );
      if (!targetPanel) {
        programmaticCarouselIndex = null;
        programmaticCarouselDistance = null;
        return;
      }

      const targetRect = targetPanel.getBoundingClientRect();
      const targetCenter =
        targetRect.left - carouselRect.left + carousel.scrollLeft + targetRect.width / 2;
      const targetDistance = Math.abs(targetCenter - viewportCenter);
      if (targetDistance <= 6) {
        programmaticCarouselIndex = null;
        programmaticCarouselDistance = null;
        return;
      }

      if (
        programmaticCarouselDistance !== null &&
        targetDistance > programmaticCarouselDistance + 6
      ) {
        programmaticCarouselIndex = null;
        programmaticCarouselDistance = null;
      } else {
        programmaticCarouselDistance = targetDistance;
        return;
      }
    }

    let nextIndex = reader.activeStreamIndex;
    let nextDistance = Number.POSITIVE_INFINITY;

    for (const panel of panels) {
      const rawIndex = panel.dataset['streamIndex'];
      if (rawIndex === undefined) continue;

      const index = Number(rawIndex);
      if (Number.isNaN(index)) continue;

      const panelRect = panel.getBoundingClientRect();
      const panelCenter =
        panelRect.left - carouselRect.left + carousel.scrollLeft + panelRect.width / 2;
      const distance = Math.abs(panelCenter - viewportCenter);
      if (distance < nextDistance) {
        nextDistance = distance;
        nextIndex = index;
      }
    }

    if (nextIndex !== reader.activeStreamIndex) {
      skipCarouselSyncIndex = nextIndex;
      reader.activateCarouselStream(nextIndex);
    }
  }

  function scrollCarouselToActiveStream(index: number, behavior: ScrollBehavior = 'smooth'): void {
    if (!carousel) return;

    const panel = carousel.querySelector<HTMLElement>(
      `[data-cy="reader-stream-panel"][data-stream-index="${index}"]`,
    );
    if (!panel) return;

    const carouselRect = carousel.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    const currentPanelCenter =
      panelRect.left - carouselRect.left + carousel.scrollLeft + panelRect.width / 2;
    const viewportCenter = carousel.scrollLeft + carousel.clientWidth / 2;
    if (Math.abs(currentPanelCenter - viewportCenter) <= 1) {
      programmaticCarouselIndex = null;
      programmaticCarouselDistance = null;
      return;
    }

    programmaticCarouselIndex = index;
    programmaticCarouselDistance = Math.abs(currentPanelCenter - viewportCenter);

    if (typeof carousel.scrollTo === 'function') {
      carousel.scrollTo({
        left: panel.offsetLeft,
        behavior,
      });
      return;
    }

    carousel.scrollLeft = panel.offsetLeft;
    programmaticCarouselIndex = null;
    programmaticCarouselDistance = null;
  }

  function closeStream(streamId: string): void {
    if (!reader?.closeStream(streamId)) {
      return;
    }

    void waitForNextFrame().then(() => {
      if (reader) {
        scrollCarouselToActiveStream(reader.activeStreamIndex);
      }
    });
  }

  function shouldExposeTestingApi(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return new URLSearchParams(window.location.search).get('fixture') === 'navigation';
  }

  function exposeTestingApi(nextReader: ReaderState): void {
    if (!shouldExposeTestingApi()) {
      return;
    }

    (window as TestingWindow).__kjvreaderTesting = {
      openStream(bookId: number, chapter: number, verse: number | null = null): boolean {
        return (
          nextReader.openStreamRight(bookId, chapter, {
            targetVerse: verse,
            align: verse === null ? 'center' : 'top',
            history: 'replace',
          }) !== null
        );
      },
      openChapterListStream(items: ChapterListStreamInput[], title: string | null = null): boolean {
        return (
          nextReader.openChapterListStreamRight(items, {
            title,
            source: 'test',
            history: 'replace',
          }) !== null
        );
      },
      openHornerReading(): boolean {
        return nextReader.openHornerReading() !== null;
      },
      setHornerReadingDay(day: number): boolean {
        return nextReader.setHornerReadingDay(day) !== null;
      },
      toggleHornerChapterRead(bookId: number, chapter: number): void {
        const stream = nextReader.activeStream;
        if (stream) {
          nextReader.toggleHornerChapterRead(stream.id, bookId, chapter);
        }
      },
      closeStream(streamId: string): boolean {
        return nextReader.closeStream(streamId);
      },
      activateStream(index: number): boolean {
        return nextReader.activateCarouselStream(index);
      },
      flushWorkspacePersistence(): void {
        nextReader.flushWorkspacePersistence();
      },
    };
  }

  function clearTestingApi(): void {
    const testingWindow = window as TestingWindow;
    if (testingWindow.__kjvreaderTesting) {
      delete testingWindow.__kjvreaderTesting;
    }
  }

  function isEditableTarget(target: EventTarget | null): boolean {
    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    );
  }
</script>

{#if database.status.state === 'idle' || database.status.state === 'initializing'}
  <div class="loading" aria-live="polite" aria-busy="true">
    <p>{database.status.state === 'idle' ? 'Starting reader…' : 'Initializing…'}</p>
  </div>
{:else if isDownloading(database.status)}
  <div class="loading" aria-live="polite" aria-busy="true">
    <p>Downloading Bible database (first visit only)…</p>
    <progress max="100" value={database.status.progress * 100} aria-label="Download progress"></progress>
    <p>{(database.status.progress * 100).toFixed(0)}%</p>
  </div>
{:else if isError(database.status) || reader?.chapterError}
  <div class="error" role="alert">
    <p>{reader?.chapterError ?? (isError(database.status) ? database.status.message : 'Load failed')}</p>
  </div>
{:else if database.status.state === 'ready' && reader?.phase === 'ready'}
  <div class="reader-shell" data-cy="reader-shell">
    <p class="visually-hidden" aria-live="polite" aria-atomic="true">
      {reader.currentReference
        ? `${reader.currentReference.bookName} ${reader.currentReference.chapter}`
        : 'Browse Bible'}
    </p>

    {#if reader.chapterLoading}
      <p class="visually-hidden" aria-live="polite">Loading nearby chapters…</p>
    {/if}

    {#if reader.chapterError}
      <div class="error reader-error" role="alert">
        <p>{reader.chapterError}</p>
      </div>
    {/if}

    {#if reader.activeHornerDay !== null}
      <div
        class="horner-reading-status"
        data-cy="horner-reading-status"
        transition:fade={{ duration: 160 }}
      >
        <button
          type="button"
          bind:this={hornerDayBadge}
          class="horner-reading-badge"
          data-cy="horner-reading-badge"
          aria-controls={HORNER_DAY_DIALOG_ID}
          aria-expanded={hornerDayDialogOpen}
          aria-haspopup="dialog"
          aria-live="polite"
          onclick={openHornerDayDialog}
        >
          <span class="horner-reading-badge__text">Horner Reading Day {reader.activeHornerDay}</span>
          <span
            class="horner-reading-progress"
            data-cy="horner-reading-progress"
            aria-label={`Horner Reading Day ${reader.activeHornerDay} marked-read progress`}
          >
            {#each hornerProgressItems(reader) as item, index (item.key)}
              <span
                class:horner-reading-progress__dot--read={item.read}
                class="horner-reading-progress__dot"
                data-cy="horner-reading-progress-dot"
                data-index={index}
                data-read={item.read ? 'true' : 'false'}
                role="img"
                aria-label={`${item.label}: ${item.read ? 'read' : 'unread'}`}
              ></span>
            {/each}
          </span>
        </button>
      </div>
    {/if}

    <div
      bind:this={carousel}
      class="reader-stream-shell"
      data-cy="reader-stream-shell"
      role="group"
      aria-label="Chapter stream carousel"
      onpointerdown={handleCarouselInteractionStart}
      onscroll={handleCarouselScroll}
      onwheel={handleCarouselInteractionStart}
    >
      {#each reader.streams as stream, streamIndex (stream.id)}
        <section
          class:reader-stream-panel--active={streamIndex === reader.activeStreamIndex}
          class="reader-stream-panel"
          animate:flip={{ duration: 220, easing: (t) => t }}
          data-cy="reader-stream-panel"
          data-stream-active={streamIndex === reader.activeStreamIndex ? 'true' : 'false'}
          data-stream-id={stream.id}
          data-stream-index={streamIndex}
          aria-label={`Stream ${streamIndex + 1} of ${reader.streams.length}`}
          in:fly={{ x: 56, duration: 220, opacity: 0.24 }}
          out:fly={{ x: 56, duration: 180, opacity: 0.18 }}
        >
          {#if streamIndex > 0}
            <button
              type="button"
              class="reader-stream-panel__close"
              data-cy="stream-close"
              aria-label={`Close stream ${streamIndex + 1}`}
              onclick={() => closeStream(stream.id)}
            >
              <span aria-hidden="true">×</span>
            </button>
          {/if}

          <ReaderStream
            reader={reader}
            stream={stream}
            streamIndex={streamIndex}
            active={streamIndex === reader.activeStreamIndex}
            onReference={navigateToCrossReference}
          />
        </section>
      {/each}
    </div>

    {#if reader.searchOpen}
      <button
        type="button"
        class="sheet-backdrop"
        data-cy="search-backdrop"
        aria-label="Close search"
        onclick={closeSearchSheet}
      >
        <span class="visually-hidden">Close search</span>
      </button>

      <div
        id={SEARCH_SHEET_ID}
        class:search-sheet--left={reader.navHandedness === 'left'}
        class:search-sheet--right={reader.navHandedness === 'right'}
        class="reader-sheet search-sheet"
        data-cy="search-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="search-sheet-title"
        tabindex="-1"
      >
        <header class="reader-sheet__header search-sheet__header">
          <div>
            <p class="reader-sheet__eyebrow">Verse search</p>
            <h2 id="search-sheet-title">Search the KJV</h2>
            <p class="reader-sheet__hint">
              Type at least two letters. Results stay here until you clear them.
            </p>
          </div>

          <div class="search-sheet__actions">
            <button
              type="button"
              class="reader-sheet__action"
              data-cy="search-clear"
              onclick={clearSearch}
            >
              Clear
            </button>
            <button
              type="button"
              class="reader-sheet__close"
              data-cy="search-close"
              aria-label="Close search"
              onclick={closeSearchSheet}
            >
              Close
            </button>
          </div>
        </header>

        <div class="search-sheet__body">
          <label class="search-field" for="reader-search-input">
            <span class="search-field__icon" aria-hidden="true"></span>
            <input
              id="reader-search-input"
              bind:this={searchInput}
              type="search"
              class="search-field__input"
              data-cy="reader-search-input"
              placeholder="Search KJV"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              value={reader.searchQuery}
              oninput={(event) => reader?.updateSearchQuery(event.currentTarget.value)}
            />
          </label>

          {#if reader.searchQuery.trim().length < 2}
            <div class="search-sheet__results" data-cy="search-results">
              <p class="search-sheet__note">Type at least two letters to search the KJV.</p>
            </div>
          {:else if reader.searchBusy}
            <div class="search-sheet__results" data-cy="search-results">
              <p class="search-sheet__note">Searching…</p>
            </div>
          {:else if reader.searchError}
            <div class="search-sheet__results" data-cy="search-results">
              <p class="search-sheet__note search-sheet__note--error">
                {reader.searchError}
              </p>
            </div>
          {:else if reader.searchResults.length === 0}
            <div class="search-sheet__results" data-cy="search-results">
              <p class="search-sheet__note">No verses found.</p>
            </div>
          {:else}
            <SearchResultsList results={reader.searchResults} onSelect={selectSearchResult} />
          {/if}
        </div>
      </div>
    {/if}

    {#if hornerDayDialogOpen}
      <button
        type="button"
        class="sheet-backdrop horner-day-backdrop"
        data-cy="horner-day-backdrop"
        aria-label="Close Horner reading day chooser"
        onclick={() => closeHornerDayDialog({ restoreFocus: true })}
      >
        <span class="visually-hidden">Close Horner reading day chooser</span>
      </button>

      <div
        id={HORNER_DAY_DIALOG_ID}
        class="reader-sheet horner-day-dialog"
        data-cy="horner-day-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="horner-day-dialog-title"
        aria-describedby="horner-day-dialog-description"
        tabindex="-1"
      >
        <form class="horner-day-dialog__form" onsubmit={submitHornerDay}>
          <header class="reader-sheet__header horner-day-dialog__header">
            <div>
              <p class="reader-sheet__eyebrow">Horner Reading</p>
              <h2 id="horner-day-dialog-title">Choose a starting day</h2>
              <p id="horner-day-dialog-description" class="reader-sheet__hint">
                The stream will restart from the selected day and clear all current Mark Read flags.
              </p>
            </div>

            <button
              type="button"
              class="reader-sheet__close"
              data-cy="horner-day-dialog-close"
              aria-label="Close Horner reading day chooser"
              onclick={() => closeHornerDayDialog({ restoreFocus: true })}
            >
              Close
            </button>
          </header>

          <div class="horner-day-dialog__body">
            <label class="horner-day-field" for="horner-day-input">
              <span>Start from day</span>
              <input
                id="horner-day-input"
                bind:this={hornerDayInput}
                type="number"
                min="1"
                step="1"
                inputmode="numeric"
                data-cy="horner-day-input"
                value={hornerDayDraft}
                aria-invalid={hornerDayError ? 'true' : 'false'}
                aria-describedby={hornerDayError
                  ? 'horner-day-error horner-day-dialog-description'
                  : 'horner-day-dialog-description'}
                oninput={(event) => updateHornerDayDraft(event.currentTarget.value)}
              />
            </label>

            {#if hornerDayError}
              <p
                id="horner-day-error"
                class="horner-day-dialog__error"
                data-cy="horner-day-error"
                role="alert"
              >
                {hornerDayError}
              </p>
            {/if}
          </div>

          <footer class="horner-day-dialog__footer">
            <button type="submit" class="reader-sheet__action" data-cy="horner-day-submit">
              Start Reading
            </button>
          </footer>
        </form>
      </div>
    {/if}

    {#if updateStatusMessage}
      <p
        class:reader-floating-status--error={updateStatusIsError}
        class:reader-floating-status--left={reader.navHandedness === 'left'}
        class:reader-floating-status--right={reader.navHandedness === 'right'}
        class="reader-floating-status"
        data-cy="reader-update-status"
        aria-live="polite"
        role={updateStatusIsError ? 'alert' : 'status'}
      >
        {updateStatusMessage}
      </p>
    {/if}

    {#if reader.streams.length > 1}
      <p
        class:reader-stream-badge--left={reader.navHandedness === 'right'}
        class:reader-stream-badge--right={reader.navHandedness === 'left'}
        class="reader-stream-badge"
        data-cy="reader-stream-badge"
        aria-live="polite"
        transition:fade={{ duration: 160 }}
      >
        {reader.activeStreamIndex + 1} of {reader.streams.length}
      </p>
    {/if}

    {#if !reader.navigatorOpen && !reader.searchOpen}
      <div
        class:fab-cluster--left={reader.navHandedness === 'left'}
        class:fab-cluster--right={reader.navHandedness === 'right'}
        class="fab-cluster"
        data-cy="fab-cluster"
        data-hand={reader.navHandedness}
      >
        <div class="settings-menu">
          <button
            type="button"
            bind:this={settingsFab}
            class="settings-fab"
            data-cy="settings-fab"
            aria-label="Open reader settings"
            aria-expanded={reader.settingsOpen}
            aria-haspopup="menu"
            aria-controls={SETTINGS_MENU_PANEL_ID}
            onclick={toggleSettingsMenu}
          >
            <span class="settings-fab__icon" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>

          {#if reader.settingsOpen}
            <div
              id={SETTINGS_MENU_PANEL_ID}
              class="settings-menu-panel"
              data-cy="settings-menu-panel"
              role="menu"
            >
              <button
                type="button"
                bind:this={settingsMenuFirstItem}
                class="settings-menu-panel__item"
                data-cy="settings-menu-check-updates"
                role="menuitem"
                onclick={checkForUpdates}
              >
                Check For Updates
              </button>
              <button
                type="button"
                class="settings-menu-panel__item"
                data-cy="settings-menu-switch-hands"
                role="menuitem"
                onclick={switchHands}
              >
                Switch Hands
              </button>
              <button
                type="button"
                class="settings-menu-panel__item"
                data-cy="settings-menu-horner-reading"
                role="menuitem"
                onclick={openHornerReading}
              >
                Horner Reading
              </button>
            </div>
          {/if}
        </div>

        <button
          type="button"
          class="search-fab"
          data-cy="search-fab"
          aria-label="Open verse search"
          aria-expanded={reader.searchOpen}
          aria-haspopup="dialog"
          onclick={toggleSearch}
        >
          <span class="search-fab__icon" aria-hidden="true"></span>
        </button>

        <button
          type="button"
          class="nav-fab"
          data-cy="nav-fab"
          aria-label="Open chapter navigator"
          aria-expanded={reader.navigatorOpen}
          onclick={toggleNavigator}
        >
          <img class="nav-fab__icon" src="/images/jump.png" alt="" aria-hidden="true" />
        </button>
      </div>
    {/if}

    {#if reader.navigatorOpen}
      <button
        type="button"
        class="nav-backdrop"
        data-cy="nav-backdrop"
        aria-label="Close chapter navigator"
        onclick={() => reader?.closeNavigator()}
      ></button>

      <div
        class="nav-sheet"
        data-cy="nav-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="chapter-nav-title"
        tabindex="-1"
      >
        <header class="nav-sheet__header">
          <div>
            <p class="nav-sheet__eyebrow">Chapter navigator</p>
            <h2 id="chapter-nav-title">Jump to a chapter</h2>
            <p class="nav-sheet__hint">Choose a book, then tap a chapter.</p>
          </div>

          <button
            type="button"
            class="nav-sheet__close"
            aria-label="Close chapter navigator"
            onclick={() => reader?.closeNavigator()}
          >
            Close
          </button>
        </header>

        <div class="nav-grid">
          <section class="nav-panel" data-cy="nav-books" aria-labelledby="chapter-nav-books-title">
            <header class="nav-panel__header">
              <h3 id="chapter-nav-books-title">Books</h3>
              <p>Canon order</p>
              <div class="nav-tabs" aria-label="Testament tabs">
                <button
                  type="button"
                  class="nav-tab"
                  class:is-selected={reader.draftTestament === 'OT'}
                  data-cy="testament-tab-ot"
                  onclick={() => reader?.chooseTestament('OT')}
                >
                  OT
                </button>
                <button
                  type="button"
                  class="nav-tab"
                  class:is-selected={reader.draftTestament === 'NT'}
                  data-cy="testament-tab-nt"
                  onclick={() => reader?.chooseTestament('NT')}
                >
                  NT
                </button>
              </div>
            </header>

            <div class="nav-list nav-list--books">
              {#each reader.draftBooks as book (book.id)}
                <button
                  type="button"
                  class="nav-option nav-option--book"
                  class:is-selected={reader.draftBook?.id === book.id}
                  onclick={() => reader?.chooseBook(book)}
                >
                  <span>{book.name}</span>
                </button>
              {/each}
            </div>
          </section>

          <section
            class="nav-panel"
            data-cy="nav-chapters"
            aria-labelledby="chapter-nav-chapters-title"
          >
            <header class="nav-panel__header">
              <h3 id="chapter-nav-chapters-title">Chapters</h3>
              <p>{reader.draftBook?.name}</p>
            </header>

            <div class="nav-list nav-list--chapters">
              {#each reader.draftChapters as chapter (chapter)}
                <button
                  type="button"
                  class="nav-option nav-option--chapter"
                  data-cy={`chapter-option-${chapter}`}
                  class:is-selected={reader.currentBook?.id === reader.draftBook?.id &&
                    reader.currentChapterNumber === chapter}
                  use:navigationIntent={{
                    onIntent: ({ intent }) => chooseChapter(chapter, intent),
                  }}
                >
                  {chapter}
                </button>
              {/each}
            </div>
          </section>
        </div>
      </div>
    {/if}
  </div>
{:else}
  <div class="loading" aria-live="polite" aria-busy="true">
    <p>Opening the selected chapter…</p>
  </div>
{/if}
